import { blogPostPath } from "@/constants/routes";
import { Badge, Button, Card, Heading, Text } from "@/design-system";
import type { BlogPostSummary } from "@/shared/blog/blogTypes";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Clock } from "lucide-react";
import { Link } from "react-router";

const formatDate = (iso: string | null): string =>
  iso ? format(new Date(iso), "dd/MM/yyyy", { locale: ptBR }) : "";

/**
 * Destaque do post pilar no hub de cluster.
 *
 * O `PostCard` da listagem não serve aqui: ele é uma peça de grade, do mesmo
 * tamanho visual dos satélites, e o hub existe justamente para dizer "leia
 * este primeiro". Então este card é largura cheia, tem o resumo (que o
 * PostCard esconde) e uma ação explícita em vez de só um título clicável.
 *
 * O título **não** é link: o card já tem um botão para o mesmo destino, e dois
 * links idênticos lado a lado só rendem repetição para quem navega por
 * tabulação ou ouve a página.
 */
export const ClusterPillarCard = ({ post }: { post: BlogPostSummary }) => (
  <Card className="flex flex-col gap-4">
    <Badge tone="brand" shape="tag" size="sm">
      Comece por aqui
    </Badge>

    <Heading level={3} className="text-pretty">
      {post.title}
    </Heading>

    {post.excerpt && (
      <Text size="body" tone="muted" className="max-w-prose">
        {post.excerpt}
      </Text>
    )}

    <div className="text-ds-ink-600 flex flex-wrap items-center gap-x-4 gap-y-1">
      {post.authorName && (
        <Text size="caption" tone="subtle">
          Por {post.authorName}
        </Text>
      )}
      {post.publishedAt && (
        <Text as="span" size="caption" tone="subtle" numeric>
          <time dateTime={post.publishedAt}>{formatDate(post.publishedAt)}</time>
        </Text>
      )}
      {post.readingTimeMin > 0 && (
        <Text size="caption" tone="subtle" className="inline-flex items-center gap-1">
          <Clock size={14} aria-hidden="true" />
          {post.readingTimeMin} min de leitura
        </Text>
      )}
    </div>

    {/* Em 360px o botão ocupa a largura toda: ação principal da seção não
        divide linha com nada nem encolhe abaixo dos 44px de alvo. */}
    <Button asChild navigates="internal" fullWidth className="sm:w-auto">
      <Link to={blogPostPath(post.slug)}>Ler o guia completo</Link>
    </Button>
  </Card>
);
